import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  CreateCase,
  GetCaseDetails,
  GetListCases,
  UpdateCase,
} from 'src/constants/ApiConstants';
import { GetApiBaseUrl } from 'src/constants/UrlConstants';
import { emitApiErrorMetrics, MetricsName } from 'src/metrics';

import {
  IViewCaseResponse,
  IViewCaseRequest,
  IGetCaseListRequest,
  IGetCaseListItem,
  IGetCaseListResponse,
  IUpdateCaseResponse,
  IUpdateCaseRequest,
  ICreateCaseRequest,
} from './models/CaseLifecycleTypes';
import * as KatalMetrics from '@amzn/katal-metrics';
import { logger } from 'src/logger';
import { parseJson } from 'src/utilities/ApiHelper';

const CASE_TAG = 'Case';
export const caseLifecycleApi = createApi({
  reducerPath: 'api',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: GetApiBaseUrl(),
    }),
  ),
  tagTypes: [CASE_TAG],
  endpoints: (builder) => ({
    /*
     * This API returns List of cases for the Regulator User.
     * TODO : It should various filters and sorting while fetching the data.
     */
    getCaseList: builder.query<
      IGetCaseListResponse,
      Partial<IGetCaseListRequest>
    >({
      query: (requestBody) => ({
        url: GetListCases.getRoute(),
        method: GetListCases.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      keepUnusedDataFor: 0,
      providesTags: [CASE_TAG],
      extraOptions: {
        maxRetries: GetListCases.numberOfRetries,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling getCaseList API with request: ${JSON.stringify(request)}`,
          );
          const { data } = await queryFulfilled;
          const dataForLogging = data?.caseDetails?.map(
            (data: IGetCaseListItem) => data.caseId,
          );
          logger.info(
            `Response for getCaseList API is: ${JSON.stringify(
              dataForLogging,
            )}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while fetching getCaseList API
            ${JSON.stringify(error)}`,
          );
          emitApiErrorMetrics(error, GetListCases.metricsPublisher);
          metric.setFailure();
        }
        GetListCases.metricsPublisher.publish(metric);
      },
      transformResponse: (response: IGetCaseListResponse) => {
        const sortedByUpdatedDate = response?.caseDetails?.sort(
          (firstItem: IGetCaseListItem, secondItem: IGetCaseListItem) => {
            return firstItem.lastUpdatedOn >= secondItem.lastUpdatedOn ? -1 : 1;
          },
        );
        return {
          ...response,
          caseDetails: sortedByUpdatedDate,
        } as IGetCaseListResponse;
      },
    }),

    /*
     * This API returns List of cases for the Regulator User.
     * TODO : It should various filters and sorting while fetching the data.
     */
    getCaseDetails: builder.query<IViewCaseResponse, Partial<IViewCaseRequest>>(
      {
        query: (requestBody) => ({
          url: GetCaseDetails.getRoute(),
          method: GetCaseDetails.method,
          body: JSON.stringify(requestBody),
          responseHandler: 'content-type',
        }),
        providesTags: [CASE_TAG],
        extraOptions: {
          maxRetries: GetCaseDetails.numberOfRetries,
        },
        async onQueryStarted(request, { queryFulfilled }) {
          const metric = new KatalMetrics.Metric.HttpRequest(
            MetricsName.BACKEND_REQUEST,
          ).withMonitor();
          try {
            logger.info(
              `Calling getCaseDetails API with request: ${JSON.stringify(
                request,
              )}`,
            );
            const { data } = await queryFulfilled;
            logger.info(
              `Successfully fetched getCaseDetails for : ${data?.caseId}`,
            );
            metric.setSuccess();
          } catch (error: any) {
            logger.error(
              `Error occured while fetching getCaseDetails API ${JSON.stringify(
                error,
              )}`,
            );
            emitApiErrorMetrics(error, GetCaseDetails.metricsPublisher);
            metric.setFailure();
          }
          GetCaseDetails.metricsPublisher.publish(metric);
        },
        transformResponse: (response: IViewCaseResponse) => {
          logger.info(`Response Recieved: ${JSON.stringify(response)}`);
          const parsedFormData = parseJson(response.data);
          const parsedCaseOverviewData = parseJson(response.caseOverviewData!);
          logger.info('Transformation of getCaseDetails API is complete.');
          return {
            ...response,
            data: parsedFormData,
            caseOverviewData: parsedCaseOverviewData,
          };
        },
      },
    ),

    createCase: builder.mutation<any, ICreateCaseRequest>({
      query: (requestBody: ICreateCaseRequest) => ({
        url:
          requestBody.isPublicPage && requestBody.isRegulatorCentralApp
            ? CreateCase.getPublicRoute()
            : CreateCase.getRoute(),
        method: CreateCase.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      invalidatesTags: [CASE_TAG],
      extraOptions: {
        maxRetries: CreateCase.numberOfRetries,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling createCase API with request: ${JSON.stringify(request)}`,
          );
          const { data } = await queryFulfilled;
          logger.info(
            `Successfully created case with the case creation response: ${JSON.stringify(
              data,
            )}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while calling createCase API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, CreateCase.metricsPublisher);
          metric.setFailure();
        }
        CreateCase.metricsPublisher.publish(metric);
      },
    }),
    updateCase: builder.mutation<IUpdateCaseResponse, IUpdateCaseRequest>({
      query: (requestBody) => ({
        url: UpdateCase.getRoute(),
        method: UpdateCase.method,
        body: JSON.stringify(requestBody),
        responseHandler: 'content-type',
      }),
      invalidatesTags: [CASE_TAG],
      extraOptions: {
        maxRetries: UpdateCase.numberOfRetries,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling UpdateCase API with request: ${JSON.stringify(request)}`,
          );
          const { data } = await queryFulfilled;
          logger.info(`Successfully updatedCase for case id: ${data.caseId}`);
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while calling UpdateCase API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, UpdateCase.metricsPublisher);
          metric.setFailure();
        }
        UpdateCase.metricsPublisher.publish(metric);
      },
    }),
  }),
});

/* Automatically generated hooks for each endpoint query */
export const {
  useGetCaseListQuery,
  useGetCaseDetailsQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  endpoints,
  reducerPath,
  middleware,
} = caseLifecycleApi;
